import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  BillStatus,
  InvoiceDto,
  OrderDto,
  TransactionType,
} from '@services/dtos'
import {
  doValidatePrecotations,
  doGetMemberSituation,
} from '@state/reducers/billingReducer'
import { doUpdateOrder } from '@state/reducers/orderReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { UpTransition } from '@components/animations'
import BillSummaryComponent from '@components/billing/BillingSummaryComponent'
import { doGetCouverture } from '@state/thunks/patientsThunk'
import { BillingDialog } from '../../containers/app/billing/BillingDialog'
import { doUpdateCouverture } from '@state/reducers/patientsReducer'
import { getMyCashRegister } from '@state/reducers/cashRegisterReducer'
import { getInvoice } from '@state/reducers/invoiceReducer'
import { isBase64 } from '@utils/dto.mapper'
import type { CouvertureDto } from '@/common/interfaces'
import useBillingWorkflow from '@hooks/useBilling'
import BillStatusDialog from '../../containers/app/Worklists/Quotation/BillStatusDialog'
import PatientInvoicesQuickPay from '@components/tables/PatientInvoicesQuickPay'

interface QuickFseActionsProps {
  order: OrderDto
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
}

const QuickFseActions: React.FC<QuickFseActionsProps> = ({
  order,
  anchorEl,
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch()

  // Local state
  const [billModeAnchor, setBillModeAnchor] = useState<HTMLElement | null>(null)
  const [openPatientPayment, setOpenPatientPayment] = useState(false)
  const [isWorking, setIsWorking] = useState(false)
  const [showBillStatusDialog, setShowBillStatusDialog] = useState(false)

  // Redux state
  const { validation, couverture, precotations } = useAppSelector(
    ({ invoice, billing, patients }) => ({
      validation: billing.validation,
      couverture: patients.patientCouverture,
      precotations: billing.precotation,
    }),
  )

  // Use shared billing workflow hook
  const {
    isValidating,
    isCreatingBill,
    billUrl,
    codeSituationSelectorUrl,
    billModesOptions,
    handleBillModeClick,
    handleCreateBill,
    handleBillSuccess,
    handleInvoiceOnly,
    ensureMemberSituation,
    ensureAllPrecotations,
    loadPrecotations,
    setBillUrl,
    setCodeSituationSelectorUrl,
    closeSummary,
  } = useBillingWorkflow({
    order,
    onInvoiceCreated: () => setOpenPatientPayment(true),
    onBillSuccess: async ({ invoices, autoSetPaid }) => {
      if (autoSetPaid) {
        await dispatch(
          doUpdateOrder({
            id: order.id,
            dto: { billStatus: BillStatus.PAID },
          }),
        )
        return
      }
      if (invoices.length > 0) {
        setOpenPatientPayment(true)
      }
    },
  })

  const handlePaymentFormClose = useCallback(() => {
    setOpenPatientPayment(false)
    setShowBillStatusDialog(true)
  }, [])

  const handleBillStatusConfirm = async (status: BillStatus) => {
    try {
      await dispatch(
        doUpdateOrder({
          id: order.id,
          dto: { billStatus: status },
        }),
      ).unwrap()

      dispatch(
        enqueueSnackbar({
          message: 'Statut de facturation mis à jour',
          options: { variant: 'success' },
        }),
      )
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors de la mise à jour du statut',
          options: { variant: 'error' },
        }),
      )
      throw error
    }
  }

  useEffect(() => {
    if (!open) return
    let isMounted = true

    const loadBillingData = async () => {
      try {
        const promises: Promise<any>[] = []

        if (order.patient.id) {
          promises.push(
            dispatch(
              doGetCouverture({
                idPatient: order.patient.id,
                idVisit: order.visitId,
              }),
            ),
          )
        }

        promises.push(dispatch(getInvoice({ orderId: order.id })))
        promises.push(dispatch(getMyCashRegister()))

        if (order.attendingDoctor?.id) {
          promises.push(
            dispatch(doGetMemberSituation(order.attendingDoctor.id)),
          )
        }

        await Promise.allSettled(promises)

        if (isMounted) {
          await loadPrecotations()
        }
      } catch (error) {
        console.error('Error loading billing data:', error)
        if (isMounted) {
          dispatch(
            enqueueSnackbar({
              message: 'Erreur lors du chargement des données de facturation',
              options: { variant: 'error' },
            }),
          )
        }
      }
    }
    loadBillingData()

    return () => {
      isMounted = false
    }
  }, [
    open,
    order.id,
    order.patient.id,
    order.visitId,
    order.attendingDoctor?.id,
    dispatch,
  ])

  // Listen for code situation selector messages
  useEffect(() => {
    if (!codeSituationSelectorUrl) return

    const handleMessage = (event: MessageEvent) => {
      if (!isBase64(event.data)) return

      try {
        const decodedStr = atob(event.data)
        const obj = JSON.parse(decodedStr) as any

        if (obj.method?.name === 'SIT-gui-choix/situation') {
          if (obj.method.cancel) {
            setCodeSituationSelectorUrl('')
          } else if (obj.method.output?.selection && couverture) {
            const { id, ...rest } = couverture
            dispatch(
              doUpdateCouverture({
                id,
                dto: {
                  ...rest,
                  codeSituationAmo: obj.method.output.selection,
                } as CouvertureDto,
              }),
            ).then(() => {
              dispatch(doValidatePrecotations(precotations)).finally(() => {
                setCodeSituationSelectorUrl('')
              })
            })
          }
        }
      } catch (e) {
        console.error('Error parsing message:', e)
      }
    }

    window.addEventListener('message', handleMessage, false)
    return () => window.removeEventListener('message', handleMessage, false)
  }, [
    codeSituationSelectorUrl,
    couverture,
    precotations,
    dispatch,
    setCodeSituationSelectorUrl,
  ])

  const handleCloseMenu = () => {
    onClose()
  }

  // Handle "Paiement en amont" click
  const onInvoiceOnlyClick = async () => {
    handleCloseMenu()
    setIsWorking(true)
    await handleInvoiceOnly()
    setIsWorking(false)
  }

  // Handle "Facturation finale" click
  const onValidateClick = async () => {
    if (isWorking) return
    handleCloseMenu()
    setIsWorking(true)

    const ms = await ensureMemberSituation()
    const hasPrecots = await ensureAllPrecotations()

    if (!ms) {
      dispatch(
        enqueueSnackbar({
          message:
            'Situation du praticien introuvable. Mettez à jour la situation pour facturer.',
          options: { variant: 'error' },
        }),
      )
      setIsWorking(false)
      return
    }

    if (!hasPrecots) {
      dispatch(
        enqueueSnackbar({
          message:
            'Aucune cotation disponible. Ajoutez/enregistrez une cotation avant de facturer.',
          options: { variant: 'error' },
        }),
      )
      setIsWorking(false)
      return
    }

    if (!anchorEl) {
      dispatch(
        enqueueSnackbar({
          message: 'Impossible d\'ouvrir le menu de facturation',
          options: { variant: 'error' },
        }),
      )
      setIsWorking(false)
      return
    }

    setBillModeAnchor(anchorEl)
    setIsWorking(false)
  }

  // Handle bill mode selection
  const onBillModeSelect = async (value: number) => {
    setBillModeAnchor(null)
    await handleBillModeClick(value)
  }

  const isBillModeOpen = Boolean(billModeAnchor)

  return (
    <>
      {/* Main action menu */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        {order.patient.isForeigner ? (
          <MenuItem
            onClick={onInvoiceOnlyClick}
            disabled={isWorking || isValidating}
          >
            Règlement 100% patient
          </MenuItem>
        ) : (
          [
            <MenuItem
              key="upfront-payment"
              onClick={onInvoiceOnlyClick}
              disabled={isWorking || isValidating}
            >
              Paiement en amont
            </MenuItem>,
            <MenuItem
              key="final-billing"
              onClick={onValidateClick}
              disabled={isWorking || isValidating}
            >
              Facturation finale
            </MenuItem>,
          ]
        )}
      </Menu>

      {/* Bill mode selection popover */}
      <Popover
        open={isBillModeOpen}
        anchorEl={billModeAnchor}
        onClose={() => setBillModeAnchor(null)}
      >
        <List>
          {billModesOptions.map((item) => (
            <ListItem key={item.value}>
              <Button
                onClick={() => onBillModeSelect(item.value)}
                variant="text"
                disabled={item.disabled}
              >
                {item.label}
              </Button>
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* Validation summary dialog */}
      <Dialog open={!!validation} fullScreen TransitionComponent={UpTransition}>
        <DialogContent>
          {validation?.visite && <BillSummaryComponent data={validation} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSummary}>Fermer</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateBill}
            disabled={isCreatingBill}
          >
            Créer la facture
          </Button>
        </DialogActions>
      </Dialog>

      {/* Code situation selector iframe dialog */}
      <Dialog
        open={!!codeSituationSelectorUrl}
        onClose={() => setCodeSituationSelectorUrl('')}
        fullScreen
        TransitionComponent={UpTransition}
      >
        <DialogContent>
          <iframe src={codeSituationSelectorUrl} width="100%" height="100%" />
        </DialogContent>
      </Dialog>

      {/* Billing iframe dialog */}
      <BillingDialog
        isOpen={!!billUrl}
        onClose={() => setBillUrl('')}
        billUrl={billUrl}
        onSuccess={handleBillSuccess}
      />

      {/* Bill Status Dialog */}
      <BillStatusDialog
        open={showBillStatusDialog}
        onClose={() => setShowBillStatusDialog(false)}
        onConfirm={handleBillStatusConfirm}
      />

      {/* Patient payment form dialog */}
      <Dialog
        open={openPatientPayment}
        onClose={() => {}}
        TransitionComponent={UpTransition}
        maxWidth="lg"
        fullScreen
      >
        {openPatientPayment && (
          <DialogContent>
            <div className="flex justify-end mb-2">
              <Button
                variant="contained"
                onClick={() => handlePaymentFormClose()}
              >
                Terminer
              </Button>
            </div>
            <PatientInvoicesQuickPay patientId={order.patient.id} />
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

export default QuickFseActions
